import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Check, ChevronDown, RotateCcw, ThumbsUp, ThumbsDown, Syringe, Search, Activity } from "lucide-react";
import { buildTimeline } from "@/lib/preventionData";

const BLUE = "#4C9EDA";
const DEEP = "#2E6FA3";
const INK = "#122534";
const SOFT = "rgba(18,37,52,0.6)";
const ICON = { screening: Search, vaccine: Syringe, check: Activity };

export default function Timeline({ data, patch, onEntrance, onRedo }) {
  const bands = useMemo(() => buildTimeline(data.age, data.sex, data.flags), [data.age, data.sex, data.flags]);
  const [open, setOpen] = useState(bands[0]?.items[0]?.k || null);
  const done = data.done || {};
  const total = bands[0]?.items.length || 0;
  const doneNow = (bands[0]?.items || []).filter((i) => done[i.k]).length;

  return (
    <div className="w-full" data-testid="prevention-timeline">
      <button onClick={onEntrance} className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-[12px] font-semibold mb-7 transition-transform hover:-translate-x-0.5"
        style={{ background: "rgba(255,255,255,0.9)", border: "1px solid rgba(18,37,52,0.08)", color: INK }} data-testid="timeline-to-entrance">
        <ArrowLeft size={13} /> Back to the entrance
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-9 items-start">
        <div className="lg:col-span-4 lg:sticky lg:top-24">
          <p className="text-[10px] tracking-[0.24em] uppercase" style={{ color: "rgba(18,37,52,0.4)" }}>Your calendar, {data.age} onwards</p>
          <h2 className="font-display text-[30px] sm:text-[40px] leading-[1.04] mt-3" style={{ color: INK }}>
            {doneNow >= total && total > 0 ? "You are up to date for this year." : `${total - doneNow} thing${total - doneNow === 1 ? "" : "s"} worth doing this year.`}
          </h2>
          <p className="text-[14.5px] mt-4" style={{ color: SOFT }}>Everything below is placed by your age and what you told me. Items move earlier when a risk factor applies, and each one carries the case against it as well as the case for.</p>

          <div className="mt-7 rounded-[1.4rem] px-5 py-5" style={{ background: "#fff", border: "1px solid rgba(18,37,52,0.08)", boxShadow: "0 30px 60px -50px rgba(18,37,52,0.8)" }}>
            <div className="flex items-baseline gap-3">
              <p className="font-display text-3xl tabular-nums" style={{ color: DEEP }}>{doneNow}<span className="text-lg" style={{ color: "rgba(18,37,52,0.3)" }}>/{total}</span></p>
              <p className="text-[12.5px]" style={{ color: SOFT }}>marked done for this year</p>
            </div>
            <div className="h-2 rounded-full mt-3 overflow-hidden" style={{ background: "rgba(18,37,52,0.08)" }}>
              <motion.span className="block h-full rounded-full" animate={{ width: `${total ? (doneNow / total) * 100 : 0}%` }} transition={{ duration: 0.5 }} style={{ background: `linear-gradient(90deg, ${BLUE}, ${DEEP})` }} />
            </div>
          </div>

          {(data.flags || []).length > 0 && (
            <div className="mt-4 rounded-[1.4rem] px-5 py-4" style={{ background: `${BLUE}14`, border: `1px solid ${BLUE}33` }} data-testid="timeline-flags">
              <p className="text-[10px] tracking-[0.18em] uppercase font-semibold" style={{ color: DEEP }}>what moved things earlier</p>
              <p className="text-[13.5px] mt-1.5" style={{ color: INK }}>{data.flags.length} risk factor{data.flags.length === 1 ? "" : "s"} you told me about. Anything they affect now opens up to ten years sooner.</p>
            </div>
          )}

          <button onClick={onRedo} className="mt-5 text-[11px] inline-flex items-center gap-1.5" style={{ color: "rgba(18,37,52,0.4)" }} data-testid="prevention-redo">
            <RotateCcw size={10} /> answer differently
          </button>
        </div>

        <div className="lg:col-span-8 relative">
          <span className="absolute left-[9px] top-6 bottom-6 w-px" style={{ background: `linear-gradient(180deg, ${DEEP}, ${BLUE}55, transparent)` }} />
          <div className="space-y-9">
            {bands.map((b, bi) => (
              <div key={b.label} className="relative pl-8" data-testid={`band-${bi}`}>
                <span className="absolute left-0 top-1.5 w-[19px] h-[19px] rounded-full flex items-center justify-center" style={{ background: bi === 0 ? DEEP : "#fff", border: `2px solid ${DEEP}` }}>
                  {bi === 0 && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
                </span>
                <p className="font-display text-[24px] sm:text-[28px] leading-none" style={{ color: INK }}>{b.label}</p>
                <p className="text-[12.5px] mt-1.5" style={{ color: "rgba(18,37,52,0.45)" }}>{b.note}</p>

                <div className="mt-4 space-y-2">
                  {b.items.map((it, i) => {
                    const on = open === it.k;
                    const Ic = ICON[it.kind] || Activity;
                    const isDone = !!done[it.k];
                    return (
                      <motion.div key={it.k} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: Math.min(i * 0.04, 0.3) }}
                        className="rounded-[1.4rem] overflow-hidden" style={{ background: on ? "#fff" : "rgba(255,255,255,0.72)", border: `1px solid ${on ? `${BLUE}55` : "rgba(18,37,52,0.07)"}`, boxShadow: on ? "0 34px 66px -52px rgba(18,37,52,0.9)" : "none" }}
                        data-testid={`item-${it.k}`}>
                        <div className="flex items-center gap-3 px-5 py-4">
                          <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${BLUE}1A`, color: DEEP }}><Ic size={15} /></span>
                          <button onClick={() => setOpen(on ? null : it.k)} className="flex-1 min-w-0 text-left">
                            <p className="font-display text-[19px] leading-tight" style={{ color: isDone ? "rgba(18,37,52,0.45)" : INK, textDecoration: isDone ? "line-through" : "none" }}>{it.name}</p>
                            <p className="text-[11.5px] mt-0.5" style={{ color: "rgba(18,37,52,0.45)" }}>{it.every}{bi === 0 && it.kind === "screening" && it.start <= data.age - 3 ? " · check when you last had this" : ""}</p>
                          </button>
                          {bi === 0 && (
                            <button onClick={() => patch((d) => ({ done: { ...(d.done || {}), [it.k]: !isDone } }))}
                              className="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
                              style={isDone ? { background: DEEP, color: "#fff" } : { border: "1.5px solid rgba(18,37,52,0.2)" }} data-testid={`item-done-${it.k}`}>
                              {isDone && <Check size={13} />}
                            </button>
                          )}
                          <ChevronDown size={14} onClick={() => setOpen(on ? null : it.k)} style={{ color: "rgba(18,37,52,0.3)", transform: on ? "rotate(180deg)" : "none", transition: "transform 0.3s", cursor: "pointer" }} />
                        </div>
                        <AnimatePresence initial={false}>
                          {on && (
                            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                              <div className="px-5 pb-5 space-y-3">
                                <div>
                                  <p className="text-[10px] tracking-[0.16em] uppercase" style={{ color: "rgba(18,37,52,0.4)" }}>what it looks for</p>
                                  <p className="text-[14.5px] mt-1 leading-relaxed" style={{ color: INK }}>{it.look}</p>
                                </div>
                                <div>
                                  <p className="text-[10px] tracking-[0.16em] uppercase" style={{ color: "rgba(18,37,52,0.4)" }}>what actually happens</p>
                                  <p className="text-[14.5px] mt-1 leading-relaxed" style={{ color: INK }}>{it.involves}</p>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 pt-1">
                                  <div className="rounded-2xl px-4 py-3.5" style={{ background: "rgba(76,158,218,0.12)" }}>
                                    <p className="text-[10px] tracking-[0.14em] uppercase font-semibold inline-flex items-center gap-1.5" style={{ color: DEEP }}><ThumbsUp size={10} /> the case for</p>
                                    <p className="text-[13.5px] mt-1.5 leading-relaxed" style={{ color: INK }}>{it.pro}</p>
                                  </div>
                                  <div className="rounded-2xl px-4 py-3.5" style={{ background: "rgba(196,85,63,0.1)" }}>
                                    <p className="text-[10px] tracking-[0.14em] uppercase font-semibold inline-flex items-center gap-1.5" style={{ color: "#A8452F" }}><ThumbsDown size={10} /> the case against</p>
                                    <p className="text-[13.5px] mt-1.5 leading-relaxed" style={{ color: INK }}>{it.con}</p>
                                  </div>
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
